import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  Divider,
  Switch,
  message,
  Alert,
  TimePicker,
  Checkbox,
  Spin,
  Empty
} from "antd";
import { Company, CompanyStatus, UpdateCompanyDto } from "../../../types/company.types";
import companyService from "../../../services/company/company.service";
import {
  BuildOutlined,
  SaveOutlined,
  PhoneOutlined,
  MailOutlined,
  BankOutlined,
  NumberOutlined,
  SettingOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import "./styles.css";
import moment from "moment";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import FooterSection from "../../shared/components/footerSection";

const { Option } = Select;
const { TextArea } = Input;

const EditCompany = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [useFixedShift, setUseFixedShift] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const { t } = useTranslation();

  // Şirket verilerini yükleme
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setLoading(true);
        const data = await companyService.getCompanyById(Number(id));
        setCompany(data);

        // Form değerlerini ayarla
        const formValues = {
          ...data,
          // Tarihleri moment nesnelerine çevir
          settings: {
            ...data.settings,
            fixedShiftStartTime: data.settings?.fixedShiftStartTime
              ? moment(data.settings.fixedShiftStartTime, "HH:mm")
              : undefined,
            fixedShiftEndTime: data.settings?.fixedShiftEndTime
              ? moment(data.settings.fixedShiftEndTime, "HH:mm")
              : undefined,
          }
        };

        form.setFieldsValue(formValues);

        // useFixedShift state'ini güncelle
        setUseFixedShift(data.settings?.useFixedShift ?? false);
      } catch (error: any) {
        handleApiError(error, t);
        setErrorMessage("Şirket bilgileri yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCompanyData();
    }
  }, [id, form, t]);

  // Şirket güncelleme işlemi
  const handleSubmit = async (values: UpdateCompanyDto) => {
    try {
      setSubmitting(true);
      setErrorMessage(null);

      // Vardiya zamanlarını string formatına dönüştürme
      if (values.settings?.useFixedShift && values.settings?.fixedShiftStartTime) {
        // Moment nesnesini kontrol et ve format metodu kullan
        const startTime = values.settings.fixedShiftStartTime as any;
        values.settings.fixedShiftStartTime = typeof startTime === 'object' && startTime.format
          ? startTime.format("HH:mm")
          : startTime as string;
      }

      if (values.settings?.useFixedShift && values.settings?.fixedShiftEndTime) {
        // Moment nesnesini kontrol et ve format metodu kullan
        const endTime = values.settings.fixedShiftEndTime as any;
        values.settings.fixedShiftEndTime = typeof endTime === 'object' && endTime.format
          ? endTime.format("HH:mm")
          : endTime as string;
      }

      // Şirket güncelleme API çağrısı
      await companyService.updateCompany(Number(id), values);
      message.success("Şirket başarıyla güncellendi!");
      navigate(`${all_routes.superAdminCompanyDetails.replace(':id', id?.toString() || '')}`);
    } catch (error: any) {
      handleApiError(error, t);
      setErrorMessage("Şirket güncellenirken bir hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  // Hafta günleri
  const weekDays = [
    { label: "Pazartesi", value: "Monday" },
    { label: "Salı", value: "Tuesday" },
    { label: "Çarşamba", value: "Wednesday" },
    { label: "Perşembe", value: "Thursday" },
    { label: "Cuma", value: "Friday" },
    { label: "Cumartesi", value: "Saturday" },
    { label: "Pazar", value: "Sunday" },
  ];

  if (loading) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <Spin size="large" />
        <p className="mt-3">Şirket bilgileri yükleniyor...</p>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <Empty
          description="Şirket bulunamadı"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
        <Button
          type="primary"
          onClick={() => navigate(all_routes.superAdminCompanies)}
          style={{ marginTop: 16 }}
        >
          Şirketlere Dön
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Şirket Düzenle</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminCompanies}>Şirketler</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={`${all_routes.superAdminCompanies}/${id}`}>{company.name}</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Düzenle
                  </li>
                </ol>
              </nav>
            </div>
            <div className="ms-2">
              <Link
                to={`${all_routes.superAdminCompanyDetails.replace(':id', id?.toString() || '')}`}
                className="btn btn-outline-primary"
              >
                <ArrowLeftOutlined /> Geri
              </Link>
            </div>
          </div>
          {/* /Breadcrumb */}

          {errorMessage && (
            <Alert
              message="Hata"
              description={errorMessage}
              type="error"
              showIcon
              closable
              className="mb-4"
            />
          )}

          <Card className="company-list-card">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="company-form"
              requiredMark="optional"
            >
              {/* Temel Bilgiler */}
              <div className="form-section">
                <h3 className="section-title">
                  <BuildOutlined /> Temel Bilgiler
                </h3>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label="Şirket Adı"
                      rules={[
                        { required: true, message: "Şirket adı gereklidir" },
                      ]}
                    >
                      <Input placeholder="Şirket adını girin" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="email" label="E-posta">
                      <Input
                        placeholder="E-posta adresini girin"
                        prefix={<MailOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="phone" label="Telefon">
                      <Input
                        placeholder="Telefon numarasını girin"
                        prefix={<PhoneOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="status" label="Durum">
                      <Select>
                        <Option value={CompanyStatus.ACTIVE}>Aktif</Option>
                        <Option value={CompanyStatus.INACTIVE}>Pasif</Option>
                        <Option value={CompanyStatus.DELETED}>
                          Silindi
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="address" label="Adres">
                      <TextArea
                        placeholder="Şirket adresini girin"
                        rows={3}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Vergi Bilgileri */}
              <div className="form-section">
                <h3 className="section-title">
                  <BankOutlined /> Vergi Bilgileri
                </h3>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="taxNumber"
                      label="Vergi No"
                      rules={[
                        { required: true, message: "Vergi no gereklidir" },
                      ]}
                    >
                      <Input
                        placeholder="Vergi numarasını girin"
                        prefix={<NumberOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="taxOffice"
                      label="Vergi Dairesi"
                      rules={[
                        { required: true, message: "Vergi dairesi gereklidir" },
                      ]}
                    >
                      <Input placeholder="Vergi dairesini girin" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Sistem Ayarları */}
              <div className="form-section">
                <h3 className="section-title">
                  <SettingOutlined /> Şirket Ayarları
                </h3>
                <Row gutter={16}>
                  {/* <Col xs={24} md={8}>
                    <Form.Item
                      name={["settings", "workOnHolidays"]}
                      label="Resmi Tatillerde Çalışma"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name={["settings", "workOnWeekends"]}
                      label="Hafta Sonları Çalışma"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col> */}
                  <Col xs={24} md={12}>
                    <Form.Item
                      name={["settings", "lateTolerance"]}
                      label="Geç Kalma Toleransı"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.settings?.lateTolerance !==
                        currentValues.settings?.lateTolerance
                      }
                    >
                      {({ getFieldValue }) => {
                        return getFieldValue(["settings", "lateTolerance"]) ? (
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item
                                name={["settings", "lateTolerationMinutes"]}
                                label="Geç Kalma Tolerans Süresi (Dakika)"
                              >
                                <Input type="number" min={0} />
                              </Form.Item>
                            </Col>
                          </Row>
                        ) : null;
                      }}
                    </Form.Item>
                  </Col>
                </Row>



                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name={["settings", "sendLeaveRequestEmails"]}
                      label="İzin Talepleri için E-posta Bildirimi"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name={["settings", "ipControl"]}
                      label="IP Kontrolü"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name={["settings", "annualLeaveLimit"]}
                      label="Yıllık İzin Limiti (Gün)"
                    >
                      <Input type="number" min={0} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">Vardiya Ayarları</Divider>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name={["settings", "shiftMode"]}
                      label="Vardiya Modu"
                    >
                      <Select>
                        <Option value="assignment">Atama Bazlı</Option>
                        <Option value="fixed">Sabit</Option>
                        <Option value="both">Her İkisi</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  {/* <Col xs={24} md={12}>
                    <Form.Item
                      name="defaultBreakDuration"
                      label="Varsayılan Mola Süresi (Dakika)"
                    >
                      <Input type="number" min={0} />
                    </Form.Item>
                  </Col> */}
                </Row>

                {/* <Form.Item
                  name={["settings", "useFixedShift"]}
                  label="Sabit Vardiya Kullan"
                  valuePropName="checked"
                >
                  <Switch
                    onChange={(checked) => setUseFixedShift(checked)}
                  />
                </Form.Item> */}

                {/* {useFixedShift && (
                  <>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name={["settings", "fixedShiftDays"]}
                          label="Sabit Vardiya Günleri"
                        >
                          <Checkbox.Group options={weekDays} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={["settings", "fixedShiftStartTime"]}
                          label="Sabit Vardiya Başlangıç Saati"
                        >
                          <TimePicker
                            format="HH:mm"
                            placeholder="Başlangıç saati"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name={["settings", "fixedShiftEndTime"]}
                          label="Sabit Vardiya Bitiş Saati"
                        >
                          <TimePicker
                            format="HH:mm"
                            placeholder="Bitiş saati"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )} */}
              </div>

              {/* Form İşlemleri */}
              <div className="d-flex justify-content-end">
                <Button
                  className="me-2"
                  onClick={() => navigate(`${all_routes.superAdminCompanyDetails.replace(':id', id?.toString() || '')}`)}
                >
                  İptal
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  Değişiklikleri Kaydet
                </Button>
              </div>
            </Form>
          </Card>
        </div>
        <div>
        </div>
        <FooterSection />
      </div>
    </>
  );
};

export default EditCompany; 